import { useState, useEffect, useRef, Fragment } from 'react';
import ShadowCard from 'components/common/cards/ShadowCard';
import useValidationConsolidator from 'hooks/useValidationConsolidator';
import { Checkbox, Stack, FormControlLabel, Typography, Divider, Link } from '@newulife/common';
import { useRecoilValue, useRecoilState } from 'recoil';
import replaceLabelPlaceholders from 'helpers/replaceLabelPlaceholders';
import { formState, inputState } from 'state';
import terms from 'api/terms';
import { find, every } from 'lodash';
import product from '../../../../api/product';
import { getEntryByContentTypeAndLocale } from 'api/contentful';
import TermsPageModal from 'components/common/modals/TermsModal';

function TermsSection(props) {
  const isLoading = useRef(false);
  const [form, setForm] = useRecoilState(formState);
  const [inputs, setInputs] = useRecoilState(inputState);
  const { requiredOptions = [], optionalOptions = [], termsData = null } = form;
  const [marketingTerms, setMarketingTerms] = useState([]); // Used for Korea only
  const hasRequiredOptions = !!requiredOptions.length;
  const [modalOpen, setModalOpen] = useState({ open: false, modalProps: {} });
  const { countryInfo, locale } = useRecoilValue(formState);
  const isKoreaTerms = countryInfo?.iso2 === 'KR';
  const { config, data = {}, isValid } = props;
  const fields = requiredOptions?.map?.((o) => o.TermId) || [];
  const { title, sectionList } = data;

  function allFieldsValidApiCall(validationValue) {
    isValid(validationValue);
  }

  const arrayOfInputValidationFunctions = useValidationConsolidator(fields, allFieldsValidApiCall);

  const allChecked = every(form?.terms, 'IsAgreedTo');

  const handleCheckAll = (e) => {
    if (allChecked) {
      document.querySelectorAll('.js-check').forEach((el) => {
        if (el.querySelector('input').checked) el.querySelector('input').click();
      });
    } else {
      document.querySelectorAll('.js-check').forEach((el) => {
        if (!el.querySelector('input').checked) el.querySelector('input').click();
      });
    }
  };

  const handleChange = (idx, idx1, field) => {
    const validationIndex = idx === 0 ? idx1 : idx1 + requiredOptions.length; // don't @ me

    // Set Terms Values
    setForm((prev) => {
      const terms =
        prev.terms?.map?.((o) => {
          if (o.TermId === field?.TermId) {
            const temp = !!!o.IsAgreedTo;
            if (idx === 0 && hasRequiredOptions)
              arrayOfInputValidationFunctions[validationIndex]?.(temp); // validate required fields only
            return { ...o, IsAgreedTo: temp };
          }

          return o;
        }) || [];

      return {
        ...prev,
        terms,
      };
    });
  };

  const getTerms = async () => {
    if (isLoading.current) return;

    isLoading.current = true;

    const localeId = await product.getLocaleId(countryInfo.country, locale);

    const [termsRes, contentfulEnrollmentTerms] = await Promise.all([
      terms.post(localeId || 1, countryInfo?.country?.toUpperCase()),
      getEntryByContentTypeAndLocale(
        'enrollmentTermsComponent',
        locale,
        countryInfo.name.replace(' NFR', ''),
        'Terms',
      ),
    ]);

    let ct = -1;
    let contentfulAreaIds = [];
    let newMiscFields = {};
    const mapCheckboxItems = (items, isAgreedToDefault, type) =>
      items
        ?.map((o) => {
          const matchedItem = termsRes?.find((o1) => o1.AreaId === o?.fields?.byDesignAreaId) || {
            IsAgreedTo: false,
            TermId: ct--,
            AreaId: -99999,
          };
          if (
            matchedItem?.MiscField &&
            matchedItem.MiscField !== 'undefined' &&
            matchedItem?.MiscField !== null
          ) {
            newMiscFields[matchedItem.MiscField] = false;
          }
          contentfulAreaIds.push(o?.fields?.byDesignAreaId);

          return {
            TermId: matchedItem.TermId,
            IsAgreedTo: isAgreedToDefault,
            ...(matchedItem?.MiscField && { MiscField: matchedItem.MiscField }),
            AreaId: matchedItem.AreaId,
            ...(matchedItem?.DocumentURL && { DocumentURL: matchedItem.DocumentURL }),
            ...o.fields,
            type,
          };
        })
        .filter(Boolean) || [];

    const requiredCheckboxItems = mapCheckboxItems(
      contentfulEnrollmentTerms?.fields?.requiredCheckboxItems,
      false,
      'required',
    );
    const optionalCheckboxItems = mapCheckboxItems(
      contentfulEnrollmentTerms?.fields?.optionalCheckboxItems,
      !!config?.preCheckOptionalTerms,
      'optional',
    );

    // Collect unmatched terms from termsRes for Korea marketing terms
    let marketingTermsWithDefaults = [];
    if (isKoreaTerms) {
      const unmatchedTerms =
        termsRes?.filter((term) => !contentfulAreaIds.includes(term.AreaId)) || [];

      marketingTermsWithDefaults = unmatchedTerms.map((term) => {
        if (term?.MiscField && term.MiscField !== 'undefined' && term.MiscField !== null) {
          newMiscFields[term.MiscField] = false;
        }

        return {
          TermId: term.TermId,
          IsAgreedTo: true,
          AreaId: term.AreaId,
          MiscField: term.MiscField,
          type: 'marketing',
        };
      });
      setMarketingTerms(unmatchedTerms);
    }

    setForm((prev) => {
      const combinedTerms = [
        ...(requiredCheckboxItems || []),
        ...(optionalCheckboxItems || []),
      ].map((o, idx) => {
        const existingTerm = prev?.terms?.find((term) => term.TermId === o?.TermId);

        if (existingTerm) {
          if (o.type === 'required') {
            arrayOfInputValidationFunctions[idx](false); // validate required fields only
          }
          return { ...existingTerm, IsAgreedTo: false };
        } else {
          return o;
        }
      });

      return {
        ...prev,
        terms: [...combinedTerms, ...marketingTermsWithDefaults],
        requiredOptions: requiredCheckboxItems,
        optionalOptions: optionalCheckboxItems,
        termsData: contentfulEnrollmentTerms,
      };
    });

    isLoading.current = false;
  };

  useEffect(() => {
    getTerms();
  }, [locale, countryInfo.name]);

  const options = (
    <div className="form-max flex-20 column">
      {[requiredOptions, optionalOptions].map(
        (o, idx) =>
          o?.length > 0 && (
            <Fragment key={`termsSection${idx}`}>
              {idx !== 0 && <Divider />}
              {idx === 0 &&
                termsData?.fields?.requiredSectionText &&
                requiredOptions?.length > 0 && (
                  <Typography variant="body3">{termsData?.fields?.requiredSectionText}</Typography>
                )}
              {idx === 1 &&
                termsData?.fields?.optionalSectionText &&
                optionalOptions?.length > 0 && (
                  <Typography variant="body3">{termsData?.fields?.optionalSectionText}</Typography>
                )}
              {idx === 2 && isKoreaTerms && marketingTerms?.length > 0 && (
                <Typography variant="body3">Marketing Terms</Typography>
              )}

              {o?.map?.((field, idx1) => (
                <FormControlLabel
                  key={`terms${idx}${idx1}`}
                  sx={{
                    alignItems: 'flex-start',
                  }}
                  data-testid={`toggle-${field?.AreaId}`}
                  control={
                    <Checkbox
                      className={`js-check ${
                        idx === 0
                          ? 'toggle-required-term'
                          : idx === 1
                          ? 'toggle-optional-term'
                          : 'toggle-marketing-term'
                      }`}
                      id={field?.TermId?.toString?.()}
                      name={field?.TermId?.toString?.()}
                      data-testid={`toggle-term-${field?.AreaId}`}
                      checked={!!find(form.terms, { TermId: field?.TermId })?.IsAgreedTo}
                      onChange={() => handleChange(idx, idx1, field)}
                    />
                  }
                  label={
                    <Stack gap={1} pt={0.3}>
                      {field?.checkboxLabel && (
                        <Typography as="label" variant="body3" htmlFor={field?.TermId}>
                          {replaceLabelPlaceholders(field?.checkboxLabel, inputs)}
                        </Typography>
                      )}
                      {field?.DocumentURL || field?.linkedFile ? (
                        <Link
                          href={field?.DocumentURL || field?.linkedFile}
                          target="_blank"
                          underline="hover"
                          sx={{
                            fontWeight: 500,
                            display: 'block',
                          }}
                        >
                          {field?.linkLabel}
                        </Link>
                      ) : field?.useModal || field?.modalText ? (
                        <Link
                          underline="hover"
                          sx={{
                            fontWeight: 500,
                            display: 'block',
                          }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setModalOpen({ open: true, modalProps: field });
                          }}
                        >
                          {field?.linkLabel}
                        </Link>
                      ) : null}
                    </Stack>
                  }
                />
              ))}
            </Fragment>
          ),
      )}
    </div>
  );

  if (!fields?.length) return null;

  return (
    <>
      <ShadowCard title={title}>
        {isKoreaTerms ? (
          <Stack gap={4}>
            <div className="form-max flex-20 column">
              {sectionList?.map((item) => (
                <p key={item}>{item}</p>
              ))}
              <Stack gap={2}>
                <FormControlLabel
                  sx={{
                    alignItems: 'flex-start',
                  }}
                  control={
                    <Checkbox id="select-all" onChange={handleCheckAll} checked={allChecked} />
                  }
                  label={
                    <Stack gap={1} pt={0.3}>
                      <Typography as="label" variant="body3" htmlFor="select-all">
                        {termsData?.fields?.selectAllCheckboxItem?.fields?.name}
                      </Typography>
                    </Stack>
                  }
                />
                <Typography variant="body3">
                  {termsData?.fields?.selectAllCheckboxItem?.fields?.disclaimerText}
                </Typography>
              </Stack>
            </div>
            <Divider />
            {options}
          </Stack>
        ) : (
          <Stack gap={4}>{options}</Stack>
        )}
      </ShadowCard>
      {modalOpen.open && (
        <TermsPageModal
          {...modalOpen.modalProps}
          close={() => setModalOpen({ open: false, modalProps: {} })}
        />
      )}
    </>
  );
}

export default TermsSection;
